// Runtime config for the backoffice SPA.
//
// One build serves both hostnames, so the environment is chosen from
// window.location.hostname rather than from a Vite build mode. Client IDs are
// public OAuth identifiers, not secrets.

export const tenantId = '6f44089f-56e6-47a6-9d4d-8c11b966da24';

interface Environment {
  clientId: string;
  apiBaseUrl: string;
}

// Prod and demo are separate Entra app registrations: `engram-backoffice` and
// `engram-backoffice-demo`. clientId and apiBaseUrl must come from the same
// environment — a token minted for one audience is rejected by the other's
// api-server.
const prod: Environment = {
  clientId: '711610b1-07fa-4cb8-8db2-97cac06bcfc1',
  apiBaseUrl: 'https://api.engram.no',
};

const demo: Environment = {
  clientId: '865d44e2-bd15-4efe-a58b-9270076c3e4c',
  apiBaseUrl: 'https://api.demo.engram.no',
};

// Exhaustive, with no default arm: the Static Web App's *.azurestaticapps.net
// hostname cannot be disabled, so a default arm ships sign-in unadministered.
// The only arm outside the map is the reserved `.localhost` dev names.
// A Map, not an object literal: `constructor`/`toString` are valid single-label
// hostnames, and a lookup on an object would return an inherited function.
const ENVIRONMENT_BY_HOSTNAME = new Map<string, Environment>([
  ['backoffice.engram.no', prod],
  ['backoffice.demo.engram.no', demo],
  ['localhost', demo],
  ['127.0.0.1', demo],
]);

/** The environment for the current hostname, or null when we do not ship to it. */
export function resolveEnvironment(): Environment | null {
  const { hostname } = window.location;
  return ENVIRONMENT_BY_HOSTNAME.get(hostname) ?? (isPortlessDevHostname(hostname) ? demo : null);
}

/** `.localhost` is a reserved TLD (RFC 6761), so no Static Web App can have these names. */
function isPortlessDevHostname(hostname: string): boolean {
  return (
    hostname === 'backoffice.engram.localhost' || hostname.endsWith('.backoffice.engram.localhost')
  );
}

function requireEnvironment(): Environment {
  const environment = resolveEnvironment();
  if (!environment) {
    throw new Error('Refusing to configure the backoffice on an unrecognised hostname');
  }
  return environment;
}

export function getClientId(): string {
  return requireEnvironment().clientId;
}

// Dev calls go to a relative /backoffice, proxied by Vite to a local api-server.
export function getApiBaseUrl(): string {
  return import.meta.env.DEV ? '' : requireEnvironment().apiBaseUrl;
}

export function getApiScope(): string {
  return `api://${getClientId()}/Backoffice.Access`;
}
